import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Observable, Subject, takeUntil } from 'rxjs';
import { IntegrationsApiService } from '../data/integrations-api.service';
import { CONNECTOR_KEY } from '../catalog/provider-catalog';
import { OAUTH_CALLBACK_TYPE, type OAuthCallbackMessage } from '../pages/oauth-callback.page';
import type { ProviderCatalogEntry, OAuthConnectMetadata } from '../domain/provider-catalog-entry';
import type { ConnectContext, ConnectorStrategy } from './connector-strategy';
import type { ConnectResult } from '../domain/connect-result';

const POPUP_FEATURES = 'width=600,height=760,menubar=no,toolbar=no';
const POPUP_POLL_MS = 500;

/**
 * Generic OAuth2 authorization-code connector. Provider-agnostic: it reads the
 * `oauth` metadata from the catalog entry (authorize URL, scopes, client id,
 * redirect, extra params), opens the provider consent popup, receives the code
 * from the generic callback page via postMessage, and exchanges it on the
 * backend (confidential client). Any OAuth2 provider is enabled by adding an
 * `oauth` block to its catalog entry + `connectorKey: oauth-redirect` — no new
 * connector class. Used by Google Calendar (and future calendar/OAuth providers).
 */
@Injectable({ providedIn: 'root' })
export class OAuthRedirectConnector implements ConnectorStrategy {
  readonly key = CONNECTOR_KEY.oauthRedirect;
  readonly introHint =
    'Vamos abrir o login do provedor em uma nova janela. Faça login e autorize o acesso solicitado para concluir a conexão.';
  readonly cancelledHint = 'Você fechou a janela sem concluir a autorização.';

  private readonly document = inject(DOCUMENT);
  private readonly api = inject(IntegrationsApiService);

  start(ctx: ConnectContext): Observable<ConnectResult> {
    const oauth = ctx.entry.oauth;
    if (!oauth) {
      return new Observable((s) => {
        s.next({ kind: 'error', message: 'Este provedor não está configurado para conexão OAuth.', recoverable: false });
        s.complete();
      });
    }
    return this.openOAuthPopup(oauth, (code, stop$, finish) => this.exchangeCode(ctx.entry, code, stop$, finish));
  }

  private openOAuthPopup(
    oauth: OAuthConnectMetadata,
    onCode: (code: string, stop$: Subject<void>, finish: (result: ConnectResult) => void) => void,
  ): Observable<ConnectResult> {
    return new Observable<ConnectResult>((subscriber) => {
      const stop$ = new Subject<void>();
      const win = this.document.defaultView;
      if (!win) {
        subscriber.next({ kind: 'error', message: 'Não foi possível acessar o contexto do navegador.', recoverable: false });
        subscriber.complete();
        return;
      }

      const state = win.crypto.randomUUID();
      const popup = win.open(this.buildAuthorizeUrl(oauth, state), 'oauth_redirect', POPUP_FEATURES);
      if (!popup) {
        subscriber.next({ kind: 'error', message: 'O navegador bloqueou a janela de login. Permita pop-ups e tente novamente.', recoverable: true });
        subscriber.complete();
        return;
      }

      let settled = false;
      let callbackReceived = false;

      const finish = (result: ConnectResult) => {
        if (settled) return;
        settled = true;
        win.clearInterval(poll);
        subscriber.next(result);
        subscriber.complete();
      };

      const onMessage = (event: MessageEvent) => {
        if (event.origin !== win.location.origin) return;
        const data = event.data as OAuthCallbackMessage | undefined;
        if (data?.type !== OAUTH_CALLBACK_TYPE || data.state !== state) return;

        callbackReceived = true;
        win.clearInterval(poll);

        if (data.error || !data.code) {
          finish({ kind: 'cancelled' });
          return;
        }
        onCode(data.code, stop$, finish);
      };

      win.addEventListener('message', onMessage);

      const poll = win.setInterval(() => {
        if (popup.closed && !callbackReceived) finish({ kind: 'cancelled' });
      }, POPUP_POLL_MS);

      return () => {
        win.removeEventListener('message', onMessage);
        win.clearInterval(poll);
        if (!popup.closed) popup.close();
        stop$.next();
        stop$.complete();
      };
    });
  }

  private buildAuthorizeUrl(oauth: OAuthConnectMetadata, state: string): string {
    const params = new URLSearchParams({
      client_id: oauth.clientId,
      redirect_uri: oauth.redirectUri,
      response_type: 'code',
      scope: oauth.scopes.join(' '),
      state,
      ...oauth.extraAuthParams,
    });
    return `${oauth.authorizeUrl}?${params.toString()}`;
  }

  private exchangeCode(
    entry: ProviderCatalogEntry,
    code: string,
    stop$: Subject<void>,
    finish: (result: ConnectResult) => void,
  ): void {
    this.api
      .connectOAuth2(entry.providerSlug, { code })
      .pipe(takeUntil(stop$))
      .subscribe({
        next: (resp) => finish({ kind: 'success', integrationId: resp.integration.id }),
        error: (err: { error?: { message?: string } }) =>
          finish({
            kind: 'error',
            message: err?.error?.message ?? `Falha ao concluir a conexão com ${entry.name}.`,
            recoverable: true,
          }),
      });
  }
}
